
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import AdminLayout from "@/layouts/AdminLayout"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { supabase } from "@/integrations/supabase/client"
import { AddAdminForm } from "@/components/user-management/AddAdminForm"
import { UsersTable } from "@/components/user-management/UsersTable"
import { SortDirection, SortField, UserProfile } from "@/types/user-management"

export default function ManageUsers() {
  const [newAdminEmail, setNewAdminEmail] = useState("")
  const [sortField, setSortField] = useState<SortField>("name")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")

  const { data: profiles, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-profiles", sortField, sortDirection],
    queryFn: async () => {
      console.log("Fetching profiles...")
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order(sortField !== "role" ? sortField : "name", { 
          ascending: sortDirection === "asc",
          nullsFirst: false
        })
      
      if (profilesError) {
        console.error("Error loading profiles:", profilesError)
        toast.error("Error loading profiles")
        throw profilesError
      }

      console.log("Profiles fetched:", profiles)

      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("*")
      
      if (rolesError) {
        console.error("Error loading user roles:", rolesError)
        toast.error("Error loading user roles")
        throw rolesError
      }

      console.log("Roles fetched:", roles)

      const combinedData: UserProfile[] = profiles.map(profile => ({
        id: profile.id,
        name: profile.name || "",
        email: profile.email || "",
        industry: profile.industry || "",
        role: roles.some(role => role.user_id === profile.id && role.role === "admin") 
          ? "admin" 
          : "user"
      }))

      console.log("Combined data:", combinedData)

      if (sortField === "role") {
        combinedData.sort((a, b) => {
          const comparison = a.role.localeCompare(b.role)
          return sortDirection === "asc" ? comparison : -comparison
        })
      }

      return combinedData
    }
  })

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const { data: userData, error: userError } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", newAdminEmail)
      .single()

    if (userError || !userData) {
      toast.error("User not found")
      return
    }

    const { error: roleError } = await supabase
      .from("user_roles")
      .insert({
        user_id: userData.id,
        role: "admin"
      })

    if (roleError) {
      console.error("Error adding admin role:", roleError)
      toast.error("Error adding admin role")
      return
    }

    toast.success("Admin role added successfully")
    setNewAdminEmail("")
    refetch()
  }

  const handleRemoveAdmin = async (userId: string) => {
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", userId)
      .eq("role", "admin")

    if (error) {
      console.error("Error removing admin role:", error)
      toast.error("Error removing admin role")
      return
    }

    toast.success("Admin role removed successfully")
    refetch()
  }

  if (error) {
    return <div className="p-4">Error loading users: {error.message}</div>
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <AdminLayout title="Manage Users">
      <div className="space-y-6">
        <AddAdminForm
          email={newAdminEmail}
          onEmailChange={setNewAdminEmail}
          onSubmit={handleAddAdmin}
        />
        <UsersTable
          profiles={profiles || []}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onRemoveAdmin={handleRemoveAdmin}
        />
      </div>
    </AdminLayout>
  )
}
